import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import Modal from '../../../../../component/modals/modal';
import ConfirmationModal from '../../../../../component/modals/confirmationModal';
import { TaskModel } from '../../../../../mobx/models/task';
import { StatusListInterface } from '../../../../../interfaces/ProjectInterface';
import { onUpdateStatus, dispatchTaskUpdates } from '../../../../../services/taskActionService';
import { captureException } from '../../../../../services/logService';

interface Props {
  loadData?: () => void;
}

export interface TaskConfirmModalHandle {
  open: (task: TaskModel, newStatus?: StatusListInterface, previousStatus?: StatusListInterface) => void;
}

const TaskConfirmModal = forwardRef<TaskConfirmModalHandle, Props>(function TaskConfirmModal({ loadData }, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState<TaskModel | null>(null);
  const [status, setStatus] = useState<StatusListInterface | null>(null);
  const [previousStatus, setPreviousStatus] = useState<StatusListInterface | null>(null);

  // Expose open method to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      open: (task: TaskModel, newStatus?: StatusListInterface, oldStatus?: StatusListInterface) => {
        setCurrentTask(task);
        setStatus(newStatus || null);
        setPreviousStatus(oldStatus || null);
        setIsOpen(true);
      }
    }),
    []
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setCurrentTask(null);
    setStatus(null);
    setPreviousStatus(null);
  }, []);

  // Confirm - complete all subtasks
  const handleConfirm = useCallback(async () => {
    if (!currentTask) return;
    try {
      setConfirmLoading(true);
      if (currentTask.project?.statusEnable && status && previousStatus) {
        onUpdateStatus(currentTask, status, previousStatus, true);
      } else {
        currentTask.setStatus(true, true);
        dispatchTaskUpdates(currentTask, true);
        loadData?.();
      }
    } catch (error) {
      captureException(error);
    } finally {
      setConfirmLoading(false);
      closeModal();
    }
  }, [currentTask, status, previousStatus, loadData, closeModal]);

  // Cancel - revert status change
  const handleCancel = useCallback(async () => {
    if (!currentTask) return;
    try {
      setCancelLoading(true);
      if (currentTask.project?.statusEnable && status && previousStatus) {
        onUpdateStatus(currentTask, status, previousStatus, false);
      } else {
        currentTask.setStatus(true, false);
        dispatchTaskUpdates(currentTask, true);
        loadData?.();
      }
    } catch (error) {
      captureException(error);
    } finally {
      setCancelLoading(false);
      closeModal();
    }
  }, [currentTask, status, previousStatus, loadData, closeModal]);

  return (
    <Modal open={isOpen} onClose={closeModal} maxWidth={'334px'}>
      <ConfirmationModal
        loading={confirmLoading}
        cancelLoading={cancelLoading}
        onOk={handleConfirm}
        onNo={handleCancel}
        onClose={closeModal}
        modalTitle='Uncompleted Subtasks'
        description='Do you want to complete all these subtasks?'
      />
    </Modal>
  );
});

TaskConfirmModal.displayName = 'TaskConfirmModal';

export default TaskConfirmModal;
